import { GameObj } from "./GameObj";
import { TileGen, TileFactory } from "./TileGen";
import { Tile, TileType } from "./Tile";
import { positionsInRadius } from "./geometry";

export class World {

    private byCoord = new Map<string, GameObj>();
    private idToPos = new Map<string, { x: number; y: number }>();
    private tiles = new Map<string, Tile>();
    private countByUnitId = new Map<string, number>();

    constructor(size: number, isOverworld: boolean, tileFactory: TileFactory | null, seed: number) {
        if (tileFactory) this.setTiles(new TileGen(size, size, isOverworld, tileFactory, seed).getTiles());
    }

    *[Symbol.iterator](): IterableIterator<{ x: number; y: number; obj: GameObj }> {
        for (const [key, obj] of this.byCoord) {
            const [xs, ys] = key.split(",");
            yield { x: Number(xs), y: Number(ys), obj };
        }
    }

    *tilesIterator(): IterableIterator<{ x: number; y: number; tile: Tile }> {
        for (const [key, tile] of this.tiles) {
            const [xs, ys] = key.split(",");
            yield { x: Number(xs), y: Number(ys), tile };
        }
    }

    private key(x: number, y: number): string {
        return x + "," + y;
    }

    private incrementCount(unitId: string): void {
        this.countByUnitId.set(unitId, (this.countByUnitId.get(unitId) ?? 0) + 1);
    }

    private decrementCount(unitId: string): void {
        const current = this.countByUnitId.get(unitId);
        if (current === undefined) return;
        if (current <= 1) {
            this.countByUnitId.delete(unitId);
        } else {
            this.countByUnitId.set(unitId, current - 1);
        }
    }

    //
    //
    //
    // #region tiles manip
    setTiles(tiles: { x: number; y: number; tile: Tile }[]): void {
        this.tiles.clear();
        for (const t of tiles) {
            this.tiles.set(this.key(t.x, t.y), t.tile);
        }
    }

    setTile(x: number, y: number, tile: Tile): void {
        this.tiles.set(this.key(x, y), tile);
    }

    getTile(x: number, y: number): Tile | undefined {
        return this.tiles.get(this.key(x, y));
    }

    hasTile(x: number, y: number): boolean {
        return this.tiles.has(this.key(x, y));
    }

    removeTile(x: number, y: number): boolean {
        return this.tiles.delete(this.key(x, y));
    }

    tileValues(): Tile[] {
        return Array.from(this.tiles.values());
    }

    get tileCount(): number {
        return this.tiles.size;
    }
    // #endregion

    //
    //
    //
    // #region obj manip
    set(x: number, y: number, obj: GameObj): void {
        const existing = this.byCoord.get(this.key(x, y));
        if (existing) {
            this.idToPos.delete(existing.id);
            this.decrementCount(existing.unitId);
        }
        this.byCoord.set(this.key(x, y), obj);
        this.idToPos.set(obj.id, { x, y });
        this.incrementCount(obj.unitId);
    }

    get(x: number, y: number): GameObj | undefined {
        return this.byCoord.get(this.key(x, y));
    }

    getById(id: string): GameObj | undefined {
        const pos = this.idToPos.get(id);
        if (!pos) return undefined;
        return this.byCoord.get(this.key(pos.x, pos.y));
    }

    getPos(id: string): { x: number; y: number } | undefined {
        return this.idToPos.get(id);
    }

    has(x: number, y: number): boolean {
        return this.byCoord.has(this.key(x, y));
    }

    hasId(id: string): boolean {
        return this.idToPos.has(id);
    }

    remove(x: number, y: number): boolean {
        const obj = this.byCoord.get(this.key(x, y));
        if (!obj) return false;
        this.byCoord.delete(this.key(x, y));
        this.idToPos.delete(obj.id);
        this.decrementCount(obj.unitId);
        return true;
    }

    removeById(id: string): boolean {
        const pos = this.idToPos.get(id);
        if (!pos) return false;
        const obj = this.byCoord.get(this.key(pos.x, pos.y));
        this.byCoord.delete(this.key(pos.x, pos.y));
        this.idToPos.delete(id);
        if (obj) this.decrementCount(obj.unitId);
        return true;
    }

    move(id: string, x: number, y: number): boolean {
        const pos = this.idToPos.get(id);
        if (!pos) return false;
        const obj = this.byCoord.get(this.key(pos.x, pos.y));
        if (!obj) return false;

        const destKey = this.key(x, y);
        const displaced = this.byCoord.get(destKey);
        if (displaced && displaced.id !== id) {
            this.idToPos.delete(displaced.id);
            this.decrementCount(displaced.unitId);
        }

        this.byCoord.delete(this.key(pos.x, pos.y));
        this.byCoord.set(destKey, obj);
        this.idToPos.set(id, { x, y });
        return true;
    }

    clear(): void {
        this.byCoord.clear();
        this.idToPos.clear();
        this.countByUnitId.clear();
    }

    getNumberOf(unitId: string): number {
        return this.countByUnitId.get(unitId) ?? 0;
    }

    get size(): number {
        return this.byCoord.size;
    }

    values(): GameObj[] {
        return Array.from(this.byCoord.values());
    }

    ids(): string[] {
        return Array.from(this.idToPos.keys());
    }

    keys(): string[] {
        return Array.from(this.byCoord.keys());
    }

    entries(): [string, GameObj][] {
        return Array.from(this.byCoord.entries());
    }

    forEach(fn: (obj: GameObj, x: number, y: number) => void): void {
        for (const [key, obj] of this.byCoord) {
            const [xs, ys] = key.split(",");
            fn(obj, Number(xs), Number(ys));
        }
    }

    filter(pred: (obj: GameObj) => boolean): GameObj[] {
        return this.values().filter(pred);
    }

    find(pred: (obj: GameObj) => boolean): GameObj | undefined {
        return this.values().find(pred);
    }
    // #endregion

    hasNearbyTile(x: number, y: number, tileType: TileType): boolean {
        return this.getTile(x, y - 1)?.type === tileType
            || this.getTile(x - 1, y)?.type === tileType
            || this.getTile(x, y + 1)?.type === tileType
            || this.getTile(x + 1, y)?.type === tileType;
    }

    hasNearbyObj(x: number, y: number, radius: number, unitId: string): boolean {
        for (const pos of positionsInRadius(x, y, radius)) {
            if (this.get(pos.x, pos.y)?.unitId === unitId) return true;
        }
        return false;
    }

    countObjectsInRadius(x: number, y: number, unitId: string, radius: number): number {
        let n = 0;
        for (const pos of positionsInRadius(x, y, radius)) {
            if (this.get(pos.x, pos.y)?.unitId === unitId) n++;
        }
        return n;
    }

}